import { alpha, Box, Modal, Stack, Typography, useColorScheme } from "@mui/material";
import { Constants } from "../../utils/constants";
import MainInput from "../forms/main_input";
import { DefaultButton, MainRadio, SuccessButton } from "..";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { useEffect, useState } from "react";
import { isValidEmail } from "../../utils/stringFormat";

export default function UserAddModal({
    open,
    onClose,
    onFinish,
    projects,
}) {
    const { mode } = useColorScheme();
    const currentMode = mode || "system";
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        minWidth: 360,
        maxWidth: 550,
        bgcolor: 'background.paper',
        border: `1px solid ${Constants.Colors[currentMode].border}`,
        borderRadius: "8px",
        boxShadow: 24,
        p: "1.5rem",
    };
    const [addDisabled, setAddDisabled] = useState(true);
    const [email, setEmail] = useState("");
    const [users, setUsers] = useState([]);

    useEffect(() => {
        setEmail("");
        setUsers([]);
        setAddDisabled(true);
    }, [open]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-user-add-title"
            aria-describedby="modal-user-add-description"
        >
            <Box sx={style}>
                <Typography id="modal-user-add-title"
                    sx={{
                        fontWeight: Constants.Styles.fontWeight.semiBold,
                        fontSize: Constants.Styles.fontSize.lg,
                        lineHeight: Constants.Styles.lineHeight.lg,
                    }}
                >
                    Add User
                </Typography>

                <Box>
                    <Typography
                        sx={{
                            fontWeight: Constants.Styles.fontWeight.medium,
                            fontSize: Constants.Styles.fontSize.sm,
                            lineHeight: Constants.Styles.lineHeight.sm,
                            marginBlock: "1.5rem 0.5rem",
                        }}
                    >Invite new user</Typography>
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        spacing={1}
                    >
                        <Box
                            sx={{
                                flexGrow: 1,
                            }}
                        >
                            <MainInput
                                placeholder={"Enter email address"}
                                fullWidth={true}
                                preIcon={<EmailOutlinedIcon />}
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (isValidEmail(e.target.value)) {
                                        setAddDisabled(false);
                                    } else {
                                        setAddDisabled(true);
                                    }
                                }}
                            />
                        </Box>
                        <DefaultButton
                            text={"Add"}
                            size="medium"
                            sx={{
                                maxWidth: 60,
                            }}
                            disabled={addDisabled}
                            onClick={() => {
                                if (!users.find((user) => user.email === email)) {
                                    setUsers([...users, { email, role: "team", projects: [], }]);
                                }
                                setEmail("");
                            }}
                        />
                    </Stack>
                </Box>
                {users.length > 0 && <Box>
                    <Typography
                        sx={{
                            fontWeight: Constants.Styles.fontWeight.medium,
                            fontSize: Constants.Styles.fontSize.sm,
                            lineHeight: Constants.Styles.lineHeight.sm,
                            marginBlock: "1.5rem 0.5rem",
                            color: Constants.Colors[currentMode].mutedForeground,
                        }}
                    >Configure access</Typography>
                    <Stack
                        spacing={"0.75rem"}
                    >
                        {users.map((user, index) => {
                            const renderRoleText = user.role === "team" ? "Team users have full access to all projects and settings." : "Select at least one project for this user to access."
                            return (
                                <Stack
                                    key={index}
                                    direction={"column"}
                                    spacing={"1.125rem"}
                                    sx={{
                                        padding: "0.75rem 1rem",
                                        backgroundColor: alpha(Constants.Colors[currentMode].secondary, 0.3),
                                        borderRadius: `calc(${Constants.Styles.radius.base} * 1)`,
                                        border: `1px solid ${Constants.Colors[currentMode].border}`,
                                    }}
                                >
                                    <Stack
                                        direction={"row"}
                                        alignItems={"center"}
                                        justifyContent={"space-between"}
                                    >
                                        <Stack
                                            direction={"row"}
                                            alignItems={"center"}
                                            justifyContent={"start"}
                                            spacing={1}
                                        >
                                            <EmailOutlinedIcon sx={{
                                                color: Constants.Colors[currentMode].mutedForeground,
                                                fontSize: "1.125rem",
                                            }} />
                                            <Typography
                                                sx={{
                                                    fontWeight: Constants.Styles.fontWeight.medium,
                                                    fontSize: Constants.Styles.fontSize.sm,
                                                    lineHeight: Constants.Styles.lineHeight.sm,
                                                    color: Constants.Colors[currentMode].foreground,
                                                }}
                                            >{user.email}</Typography>
                                        </Stack>
                                        <CloseOutlinedIcon sx={{
                                            cursor: "pointer",
                                            color: Constants.Colors[currentMode].mutedForeground,
                                            fontSize: "1.125rem",
                                        }}
                                            onClick={() => {
                                                setUsers(users.filter((u) => u.email !== user.email));
                                            }}
                                        />
                                    </Stack>
                                    <MainRadio
                                        label={"Role"}
                                        value={user.role}
                                        onChange={(e) => {
                                            setUsers(users.map((u) => u.email === user.email ? { ...u, role: e.target.value } : u));
                                        }}
                                        options={[
                                            { value: "team", label: "Team" },
                                            { value: "guest", label: "Guest" },
                                        ]}
                                    />
                                    {user.role === "guest" && <Box>
                                        <Typography
                                            sx={{
                                                fontWeight: Constants.Styles.fontWeight.normal,
                                                fontSize: Constants.Styles.fontSize.xs,
                                                lineHeight: Constants.Styles.lineHeight.xs,
                                                color: Constants.Colors[currentMode].mutedForeground,
                                            }}
                                        >Project access</Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {projects.map((p, index) => {
                                                const selected = user.projects.find((id) => id.project_id === p.project_id);
                                                return (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            paddingBlock: `calc(${Constants.Styles.spacing} * 1)`,
                                                            paddingInline: `calc(${Constants.Styles.spacing} * 2)`,
                                                            border: `1px solid ${selected ? Constants.Colors[currentMode].accent : Constants.Colors[currentMode].border}`,
                                                            borderRadius: `calc(${Constants.Styles.radius.base} - 2px)`,
                                                            marginInline: `calc(${Constants.Styles.spacing} * 0.5)`,
                                                            marginBlock: `calc(${Constants.Styles.spacing} * 0.5)`,
                                                            cursor: "pointer",
                                                            backgroundColor: selected ? alpha(Constants.Colors[currentMode].accent, 0.1) : "transparent",
                                                            color: selected ? Constants.Colors[currentMode].accent : Constants.Colors[currentMode].mutedForeground,
                                                            "&:hover": {
                                                                borderColor: selected ? Constants.Colors[currentMode].accent : Constants.Colors[currentMode].foreground,
                                                                color: selected ? Constants.Colors[currentMode].accent : Constants.Colors[currentMode].foreground,
                                                            }
                                                        }}
                                                        onClick={() => {
                                                            var up = user.projects;
                                                            if (selected) {
                                                                up = up.filter((id) => id.project_id !== p.project_id);
                                                            } else {
                                                                up.push(p);
                                                            }
                                                            console.log(up);
                                                            setUsers(users.map((u) => u.email === user.email ? { ...u, projects: up } : u));
                                                        }}
                                                    >
                                                        <Stack direction={"row"}
                                                            alignItems={"center"}
                                                            justifyContent={"center"}
                                                            spacing={0.5}
                                                        >
                                                            <Typography
                                                                sx={{
                                                                    fontSize: Constants.Styles.fontSize.xs,
                                                                    lineHeight: Constants.Styles.lineHeight.xs,
                                                                }}
                                                            >{p.name}</Typography>
                                                            {selected && <CheckOutlinedIcon sx={{
                                                                fontSize: 16,
                                                                color: Constants.Colors[currentMode].accent,
                                                            }} />}
                                                        </Stack>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </Box>}
                                    <Typography
                                        sx={{
                                            fontWeight: Constants.Styles.fontWeight.normal,
                                            fontSize: Constants.Styles.fontSize.xs,
                                            lineHeight: Constants.Styles.lineHeight.xs,
                                            color: Constants.Colors[currentMode].mutedForeground,
                                        }}
                                    >{renderRoleText}</Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Box>}
                <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"end"}
                    sx={{
                        mt: "1.5rem",
                    }}
                    spacing={2}
                >
                    <DefaultButton
                        text={"Cancel"}
                        size="medium"
                        onClick={() => {
                            onClose();
                        }}
                        sx={{
                            width: 60,
                        }}
                    />
                    <SuccessButton
                        text={"Send Invitations"}
                        size="medium"
                        disabled={users.length === 0}
                        onClick={() => {
                            onFinish(users);
                        }}
                        sx={{
                            width: 160,
                        }}
                    />
                </Stack>
            </Box>
        </Modal>
    );
}